import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export interface ContextValue {
  event: string
  description: string
}

interface Props {
  onComplete: (context: ContextValue) => void
  onSkip: () => void
  title?: string
}

/**
 * Request 4: let the respondent say why they are taking the test right now.
 * The note turns each result into a milestone on their timeline instead of an
 * anonymous point, and it is what labels the cards in their history.
 * Optional by design: skipping must never block the survey.
 */
const ContextStep: React.FC<Props> = ({ onComplete, onSkip, title }) => {
  const intl = useIntl()
  const [event, setEvent] = useState('')
  const [description, setDescription] = useState('')

  const trimmedEvent = event.trim()
  const trimmedDescription = description.trim()
  const canContinue = !!trimmedEvent || !!trimmedDescription

  const submit = () => {
    if (!canContinue) {
      onSkip()
      return
    }
    onComplete({ event: trimmedEvent, description: trimmedDescription })
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">{title ?? intl.formatMessage({ id: 'take.context.title' })}</h3>
      <p className="text-sm text-foreground-muted">
        <FormattedMessage id="take.context.subtitle" />
      </p>

      <div>
        <label htmlFor="cs-event" className="block text-sm font-medium text-foreground-muted mb-1">
          <FormattedMessage id="take.context.momentLabel" />
        </label>
        <input
          id="cs-event"
          type="text"
          value={event}
          maxLength={100}
          onChange={(e) => setEvent(e.target.value)}
          placeholder={intl.formatMessage({ id: 'take.context.momentPlaceholder' })}
          className="w-full border border-border rounded-lg px-3 py-2"
        />
      </div>

      <div>
        <label htmlFor="cs-description" className="block text-sm font-medium text-foreground-muted mb-1">
          <FormattedMessage id="take.context.whyNowLabel" />
        </label>
        <textarea
          id="cs-description"
          value={description}
          rows={3}
          maxLength={1000}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={intl.formatMessage({ id: 'take.context.whyNowPlaceholder' })}
          className="w-full border border-border rounded-lg px-3 py-2"
        />
      </div>

      <button
        type="button"
        onClick={submit}
        className="w-full bg-primary text-white font-semibold py-2.5 rounded-lg"
      >
        <FormattedMessage id="take.context.continue" />
      </button>
      <button
        type="button"
        onClick={onSkip}
        className="w-full text-sm text-foreground-muted underline"
      >
        <FormattedMessage id="take.context.skip" />
      </button>
    </div>
  )
}

export default ContextStep
